// components/CameraStream.tsx
import { useEffect, useRef } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { usePlayback } from "../../../../context/VideoPlayback";

const CameraStream = ({ url, isPlaying, index }) => {
  const videoNode = useRef(null);
  const player = useRef(null);
  const { currentTime, setCurrentTime, playbackRate, setDuration, sync } =
    usePlayback();
  useEffect(() => {
    if (videoNode.current) {
      player.current = videojs(videoNode.current, {
        autoplay: isPlaying,
        controls: true,
        muted: true,
        preload: "auto",
        responsive: true,
        sources: [{ src: url, type: "application/x-mpegURL" }],
        controlBar: {
          children: [
            "playToggle",
            "volumePanel",
            "currentTimeDisplay",
            "progressControl",
            "remainingTimeDisplay",
            "fullscreenToggle",
          ],
        },
      });

      player.current.ready(() => {
        if (isPlaying) {
          player.current.play().catch((error) => {
            console.warn("Play interrupted: ", error);
          });
        }
      });

      if (index === 0 && player.current) {
        const updateDuration = () => {
          const videoDuration = player.current.duration();
          if (!isNaN(videoDuration)) {
            setDuration(videoDuration);
          }
        };

        player.current.on("loadedmetadata", updateDuration);

        // Optional cleanup
        return () => {
          player.current.off("loadedmetadata", updateDuration);
          player.current.pause();
          player.current.dispose();
        };
      }

      // Define Screenshot Button
      const Button = videojs.getComponent("Button");

      class ScreenshotButton extends Button {
        constructor(player, options) {
          super(player, options);
          this.controlText("Screenshot");
          this.addClass("vjs-screenshot-button"); // For custom CSS if needed
        }

        handleClick() {
          takeScreenshot();
        }
      }

      class CallButton extends Button {
        constructor(player, options) {
          super(player, options);
          this.controlText("Call");
          this.addClass("vjs-call-button"); // For custom CSS if needed
        }

        handleClick() {
          alert("Call functionality triggered!");
        }
      }

      // Register Components
      videojs.registerComponent("ScreenshotButton", ScreenshotButton);
      videojs.registerComponent("CallButton", CallButton);

      // Add Buttons to Control Bar
      player.current
        .getChild("controlBar")
        .addChild(
          "ScreenshotButton",
          {},
          player.current.controlBar.children().length - 1
        );
      player.current
        .getChild("controlBar")
        .addChild(
          "CallButton",
          {},
          player.current.controlBar.children().length - 1
        );
    }

    return () => {
      if (player.current) {
        player.current.pause();
        player.current.dispose();
      }
    };
  }, [url]);

  // Sync play/pause for all
  useEffect(() => {
    if (player.current) {
      isPlaying
        ? player.current.play().catch(console.warn)
        : player.current.pause();
    }
  }, [isPlaying]);

  // ✅ ONLY the master (index 0) emits currentTime updates
  useEffect(() => {
    if (index === 0 && player.current && sync) {
      const playerInstance = player.current;
      const updateTime = () => {
        setCurrentTime(playerInstance.currentTime());
      };
      playerInstance.on("timeupdate", updateTime);
      return () => {
        playerInstance.off("timeupdate", updateTime);
      };
    }
  }, [index, sync]);

  
  // ✅ Only non-master players follow the master time
  useEffect(() => {
    if (index !== 0 && player.current && sync) {
      const videoTime = player.current.currentTime();
      if (Math.abs(videoTime - currentTime) > 0.2) {
        player.current.currentTime(currentTime);
      }
      // player.current.currentTime(currentTime);
    }
  }, [currentTime, sync, index]);
  useEffect(() => {
    if (player.current) {
      const videoTime = player.current.currentTime();
      if (Math.abs(videoTime - currentTime) > 0.2) {
        player.current.currentTime(currentTime);
      }
    }
  }, [currentTime]);

  // ✅ All players follow the global playback rate
  useEffect(() => {
    if (player.current) {
      player.current.playbackRate(playbackRate);
    }
  }, [playbackRate]);

  const takeScreenshot = () => {
    const video = videoNode.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const screenshotUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = screenshotUrl;
    link.download = "screenshot.png";
    link.click();
  };

  return (
    <div data-vjs-player>
      <video
        ref={videoNode}
        className="video-js vjs-default-skin"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      ></video>
    </div>
  );
};

export default CameraStream;
